import {
  getFloatExchange,
  getOriginExchangeRate
} from '@/apis/shopcart'
import { getCustomerExchangeRateApi } from '@/apis/user'
import { t } from '@/plugins/i18n'
import { ref, computed } from 'vue'
import { formatMoney } from '@/plugins/utils'
import store, { isJA, useSite2Station, isKO, useUser } from '@/store'
import { siteMapMonetaryUnit, countryCurrency, Site } from '@/common/const'

/** 源汇率(银行查出来的) */
export const rate: any = ref(null)
/**  源汇率 + 浮动汇率  */
export const floatingRate: any = ref(null)
/** 浮动汇率(我们配置的) */
export const floatExchangeRate: any = ref(1)
/** 一价全包浮动后的汇率 */
export const allIncludeExchangeRate:any = ref(null)

// 存在日本站查看韩国商品的情况
/** 韩元-源汇率(银行查出来的) */
export const rateKrw: any = ref(null)
/**  韩元-源汇率 + 浮动汇率  */
export const floatingRateKrw: any = ref(null)
/** 韩元-浮动汇率(我们配置的) */
export const floatExchangeRateKrw: any = ref(1)

export type currencyCode='CNY'| 'KRW'

// 人民币转站点货币或者韩元
export const calc = (rmb: any, currencyCode?:currencyCode) => {
  const _currencyCode = currencyCode ?? 'CNY'
  //   判断币种，若是为韩元币种，则取floatingRateKrw汇率
  const floatingRateVal = _currencyCode === 'KRW' ? floatingRateKrw.value : floatingRate.value
  if (!floatingRateVal) return '***'
  const value = parseFloat((parseFloat(rmb) * floatingRateVal).toFixed(2))
  //   日本站点，且支持韩国仓，若是韩国商品价格小于等于1，需要给个最小的数字1
  if (_currencyCode === 'KRW' && Number(value ?? 0) < 1 && Number(rmb ?? 0) !== 0) {
    return '1'
  }
  //   当floatingRateVal大于1，或者币种为韩元，不需要保留小数
  if (floatingRateVal >= 1 || (currencyCode === 'KRW' && isJA())) {
    return formatMoney(Math.floor(parseFloat(rmb) * floatingRateVal))
  }
  return formatMoney(value)
}

export const moneyAddCurrency = (rmb: any, currencyCode?:string) => {
  if (currencyCode === 'KRW') return `₩ ${formatMoney(rmb)}`
  return `${formatMoney(rmb)}${t('元_1')}`
}
export const bothFormat = (rmb: any): any => {
  return `${rmb}${t('元')} (${calc(rmb)}円)`
}

// 人民币转站点货币或者韩元
export const calcCeil = (rmb: any, currencyCode?:string) => {
  const _currencyCode = currencyCode ?? 'CNY'
  //   判断币种，若是为韩元币种，则取floatingRateKrw汇率
  const floatingRateVal = _currencyCode === 'KRW' ? floatingRateKrw.value : floatingRate.value
  if (!floatingRateVal) return '***'
  const value = parseFloat((parseFloat(rmb) * floatingRateVal).toFixed(2))
  //   日本站点，且支持韩国仓，若是韩国商品价格小于等于1，需要给个最小的数字1
  if (_currencyCode === 'KRW' && Number(value ?? 0) < 1 && Number(rmb ?? 0) !== 0) {
    return 1
  }
  //   当floatingRateVal大于1，或者币种为韩元，不需要保留小数
  if (floatingRateVal >= 1 || (currencyCode === 'KRW' && isJA())) {
    return formatMoney(Math.ceil(parseFloat(rmb) * floatingRateVal))
  }
  return formatMoney(value)
}

// 向上取整
function roundUpToTwoDecimal (num:number) {
  return Math.ceil(num * 100) / 100
}

// 一价全包日元转 rmb
export const calcRmb = (jpy:number|string = 0) => {
  const floatingRateVal = allIncludeExchangeRate.value
  if (!jpy) return 0
  const rmb = roundUpToTwoDecimal((+jpy / floatingRateVal))
  return rmb
}

//
export const formatMoneyRange = (value: string|number) => {
  if (typeof value === 'string' && value.includes('-')) {
    return [formatMoney(value.split('-')[0]), formatMoney(value.split('-')[1])].join('-')
  }
  return formatMoney(value)
}

// 一价全包日元转 rmb
export const calcJpyToRmb = (value: string|number) => {
  if (typeof value === 'string' && value.includes('-')) {
    return [formatMoney(calcRmb(value.split('-')[0])), formatMoney(calcRmb(value.split('-')[1]))].join('-')
  } else {
    return formatMoney(calcRmb(value))
  }
}

export default (rmb: any): any => {
  return computed(() => calc(rmb))
}

export const resetRate = () => {
  req()
  getToKrwLocalCurrencyCode()
}

/**
 * 返回当前站点下货币单元  日本站"ja" 则货币单元为円,未登录默认处于日本站
 */
export const getCurrentMonetaryUnit = () => {
  const currentSite = useSite2Station()
  const currentMonetaryUnit = siteMapMonetaryUnit.get(currentSite)

  return currentMonetaryUnit
}

// 获取国家货币
export const getCountryCurrency = () => {
  const site = useSite2Station()
  const currentCountryCurrency = countryCurrency.get(site)
  return currentCountryCurrency
}
/** 获取日本站时韩国汇率 */
export const getToKrwLocalCurrencyCode = () => {
  const stationCode = useSite2Station?.()
  //   如果不是日本站点直接不用请求
  if (stationCode !== Site.JA) return
  //   目前只有日本站才需要，展示韩元汇率
  if (store?.state.route?.user?.mainCustomerId) {
    getFloatExchange(store.state.route?.user?.mainCustomerId, 'KRW').then((res) => {
      // 英国站 美元比人民币高,后端给的7.14  1￥=7.14$
      // 后端的汇率逻辑 7.14￥=1$   1￥=200韩元  1￥ = 20日元
      // 前端金额的计算统一是乘法 人民币*汇率得到对应国家的金额  所以英国要做除法
      rateKrw.value = res.data?.originalExchangeRate
      floatingRateKrw.value = res.data?.exchangeRate
      floatExchangeRateKrw.value = res.data?.floatExchangeRate
    })
    // 日本站的未登录也需要请求该接口
  } else {
    getOriginExchangeRate(stationCode, 'KRW').then((res) => {
      // 英国站 美元比人民币高,后端给的7.14  1￥=7.14$
      // 后端的汇率逻辑 7.14￥=1$   1￥=200韩元  1￥ = 20日元
      // 前端金额的计算统一是乘法 人民币*汇率得到对应国家的金额  所以英国要做除法
      floatingRateKrw.value = Number(res.data?.exchangeRate)
      rateKrw.value = res.data?.originalExchangeRate
      floatExchangeRateKrw.value = Number(res.data?.floatExchangeRate)
    })
  }
}

export const req = () => {
  const stationCode = useSite2Station?.()
  if (store?.state.route?.user?.mainCustomerId) {
    getFloatExchange(store.state.route?.user?.mainCustomerId).then((res) => {
      // 英国站 美元比人民币高,后端给的7.14  1￥=7.14$
      // 后端的汇率逻辑 7.14￥=1$   1￥=200韩元  1￥ = 20日元
      // 前端金额的计算统一是乘法 人民币*汇率得到对应国家的金额  所以英国要做除法
      if (stationCode === Site.EN) {
        rate.value = 1 / Number(res.data?.originalExchangeRate)
        floatingRate.value = 1 / Number(res.data?.exchangeRate)
        floatExchangeRate.value = 1 / Number(res.data?.floatExchangeRate)
      } else {
        rate.value = res.data?.originalExchangeRate
        floatingRate.value = res.data?.exchangeRate
        floatExchangeRate.value = res.data?.floatExchangeRate
      }
    })
    // 获取一价全包汇率
    getCustomerExchangeRateApi({
      currencyCode: 'CNY',
      customerId: useUser()?.customerId
    }).then((res) => {
      allIncludeExchangeRate.value = Number(res.data?.allIncludeExchangeRate)
    })
  } else {
    // 接口变更 /settlement/exchangeRate/getLatestExchangeRate =》/settlement/exchangeRate/getOriginExchangeRate
    getOriginExchangeRate(stationCode).then((res) => {
      // 英国站 美元比人民币高,后端给的7.14  1￥=7.14$
      // 后端的汇率逻辑 7.14￥=1$   1￥=200韩元  1￥ = 20日元
      // 前端金额的计算统一是乘法 人民币*汇率得到对应国家的金额  所以英国要做除法
      if (stationCode === Site.EN) {
        floatingRate.value = 1 / Number(res.data?.exchangeRate)
        floatExchangeRate.value = 1 / Number(res.data?.floatExchangeRate)
        rate.value = 1 / res.data?.originalExchangeRate
      } else {
        floatingRate.value = Number(res.data?.exchangeRate)
        rate.value = res.data?.originalExchangeRate
        floatExchangeRate.value = Number(res.data?.floatExchangeRate)
        allIncludeExchangeRate.value = Number(res.data?.allIncludeExchangeRate)
      }
      // console.log(floatingRate.value, floatExchangeRate.value, rate.value)
    })

    // getExchange().then((res) => {
    //   rate.value = res.data?.exchangeRate;
    //   floatingRate.value = Number(res.data?.exchangeRate) + 1;
    // });
  }
}

// 半小时获取一次汇率
setInterval(() => {
  req()
  getToKrwLocalCurrencyCode()
}, 1000 * 60 * 30)

setTimeout(() => {
  req()
  getToKrwLocalCurrencyCode()
}, 0)

// 格式化金额
// 1,000 円
// ₩ 1,000
// $  1,000
export const formatCountryMoney = (rmb: any, currencyCode?: 'CNY'| 'KRW') => {
  let price = calc(rmb, currencyCode)
  switch (useSite2Station()) {
    case Site.JA:
      price = price + ' 円'
      break
    case Site.KO:
      price = '₩ ' + price
      break
    case Site.EN:
      price = '$ ' + price
      break
  }
  return price
}

/**
 *
 * @param money 货币价格
 * @param isThousandth 是否需要千分位
 * @returns 根据站点更换货币方向
 * isMultiGoods 是否韩国仓商品
 */
export const formatMoneyDirection = (money: any, isThousandth = false, isMultiGoods?:boolean) => {
  let showText = ''
  // 是否千分位化
  money = isThousandth ? formatMoney(money) : money
  if (isMultiGoods) {
    return money + t('韩元')
  }
  switch (useSite2Station()) {
    case Site.JA:
      showText = money + ' 円'
      break
    case Site.KO:
      showText = '₩ ' + money
      break
    case Site.EN:
      showText = '$ ' + money
      break
  }
  return showText
}

// 根据活动获取折扣价格
export const getActivePriceByStation = (): number => {
  if (isJA()) {
    return 1
  } else if (isKO()) {
    return 10
  } else {
    return 0.1
  }
}

// 是否展示元还是 韩元
export const showCurrentUnit = (isShowKorea:boolean, num?:number|string) => {
  return `${num ?? ''} ${isShowKorea ? t('韩元') : t('元')}`
}

// 展示价格加单位，用于表格中返回-加单位的问题
export const showPriceAndUnit = (price?: string|number, unit?: string) => {
  if (price === null || price === undefined || price === '-') {
    return price
  }
  return `${price} ${unit}`
}
